

















































































import { Component, Mixins, Prop } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import BasicConfiguration from "@/components/app/admin/catalogue/promotions/formComponents/basicConfiguration.vue";
import HasValidation from "@/mixins/HasValidation";
import { DiscountTypes } from "@/data/enums/promotions";
import {
  IPromotion,
  IPromotionForm,
  IPromotionProduct
} from "@/models/promotions";
import BrandsStore from "@/mixins/brandsStore";
import { DataModules } from "@/store/modules/data";

@Component({
  components: {
    BasicConfiguration,
    "selected-products": () =>
      import(
        "@/components/app/admin/catalogue/promotions/formComponents/selectedProducts.vue"
      ),
    "mandatory-products": () =>
      import(
        "@/components/app/admin/catalogue/promotions/formComponents/mandatoryProducts.vue"
      ),
    "individual-products": () =>
      import(
        "@/components/app/admin/catalogue/promotions/formComponents/individualProducts.vue"
      ),
    "availability-and-eligibility": () =>
      import(
        "@/components/app/admin/catalogue/promotions/formComponents/availabilityAndEligibility.vue"
      ),
    "discount-and-conditions": () =>
      import(
        "@/components/app/admin/catalogue/promotions/discountAndConditions.vue"
      )
  }
})
export default class AddEditPromotion extends Mixins(
  BrandsStore,
  HasValidation
) {
  public declare $refs: {
    basicConfiguration: BasicConfiguration;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Props
  @Prop({ default: null })
  public promotion!: IPromotion | null;

  @Prop({ default: false })
  public isLoading!: boolean;

  @Prop({ default: false })
  public isReloading!: boolean;

  // Data
  public form: IPromotionForm = {
    active: true,
    amount: 0,
    autoapply: false,
    billing_cycles: [],
    brand_id: this.brandId,
    code: "",
    combined: false,
    constant_discount: false,
    currencies: [],
    for_existing_clients: true,
    for_new_clients: true,
    for_upgrade_clients: true,
    global: true,
    mandatory_products: [],
    max_uses: 0,
    max_uses_per_client: 0,
    name: "",
    order_type: 0,
    pricelists: [],
    products: [],
    promo_recurring_count: 0,
    recurring: 0,
    short_description: "",
    show_on_catalog: true,
    type: DiscountTypes.PERCENTAGE_BASED
  };
  public formClone = {} as IPromotionForm;

  public isProcessing = false;
  public isFormInitiating = true;

  public async created() {
    if (!this.contentIsAvailable) return;
    this.refreshForm();
    this.isFormInitiating = false;
  }

  // Getters
  public get canManagePromotion() {
    if (this.isUpdate) return this.$userCan("update_promotion");
    return this.$userCan("create_promotion");
  }

  public onProductsChange(value) {
    // Replace with new products
    this.form.products?.splice(0, this.form.products.length, ...value);
  }

  public guardFunctionalities = [
    "staff_list_products",
    "list_product_categories"
  ];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get promotionType() {
    return this.$store.getters[
      `data/${DataModules.CATALOGUE_PROMOTIONS}/getPromotionType`
    ](this.form);
  }

  public get isUpdate() {
    return !this.$_.isEmpty(this.promotion);
  }

  public get promotionId() {
    return this.$route.params.promotionId;
  }

  public get isMoneyBased() {
    return this.form.type === DiscountTypes.MONEY_BASED;
  }

  public get isSelectedPromotion() {
    return !this.form.global && this.form.constant_discount;
  }

  public get isIndividualPromotion() {
    return !this.form.global && !this.form.constant_discount;
  }

  public get formIsValid() {
    if (this.isUpdate && !this.formIsChanged) return false;
    return !this.hasErrors;
  }

  public get formIsDisabled() {
    return (
      this.isLoading ||
      this.isFormInitiating ||
      this.isProcessing ||
      !this.formIsValid
    );
  }

  public get promoTypeIsChanged() {
    return (
      this.$_.get(this.promotion, "global") !==
        this.$_.get(this.form, "global") ||
      this.$_.get(this.promotion, "constant_discount") !==
        this.$_.get(this.form, "constant_discount")
    );
  }

  public get promotionProperties() {
    return this.$store.getters[
      `data/${DataModules.CATALOGUE_PROMOTIONS}/promotionFields`
    ](this.promotion);
  }

  public get extendedPromotionProperties() {
    return [...this.promotionProperties, "products"];
  }

  public get formIsChanged() {
    return !this.$_.isEqual(
      this.$_.pick(this.form, this.extendedPromotionProperties),
      this.$_.pick(this.formClone, this.extendedPromotionProperties)
    );
  }

  public get isDisabled() {
    return this.isProcessing || !this.canManagePromotion;
  }

  public get selectedProductsChanged() {
    if (!this.isSelectedPromotion) return false;
    if (this.form.amount !== this.formClone.amount) return true;
    if (this.form.type !== this.formClone.type) return true;
    if (this.form.min_quantity !== this.formClone.min_quantity) return true;
    if (this.form.max_quantity !== this.formClone.max_quantity) return true;
    if (this.form.currency_id !== this.formClone.currency_id) return true;
    if (
      !this.$_.isEqual(
        this.$_.sortBy(this.form.currencies),
        this.$_.sortBy(this.formClone.currencies)
      )
    )
      return true;
    if (
      !this.$_.isEqual(
        this.$_.sortBy(this.form.billing_cycles),
        this.$_.sortBy(this.formClone.billing_cycles)
      )
    )
      return true;
    if (
      !this.$_.isEqual(
        this.$_.sortBy(this.form.pricelists),
        this.$_.sortBy(this.formClone.pricelists)
      )
    )
      return true;
    return false;
  }

  public get formData() {
    const form = this.$_.cloneDeep(this.form) as any;

    if (this.isSelectedPromotion) {
      form.products = this.$_.map(form.products, (formProduct, index) => {
        const promotionProduct = this.$_.find(
          this.$_.get(this.promotion, "products", []),
          ["product_id", formProduct.product_id]
        ) as IPromotionProduct;

        return this.$_.merge(
          promotionProduct
            ? {
                id: promotionProduct.id,
                order: promotionProduct.order
              }
            : {
                order: index
              },
          formProduct,
          this.$_.pick(form, [
            "amount",
            "billing_cycles",
            "currencies",
            "currency_id",
            "pricelists",
            "type",
            "min_quantity",
            "max_quantity"
          ])
        );
      });

      delete form.amount;
      delete form.billing_cycles;
      delete form.currencies;
      delete form.currency_id;
      delete form.pricelists;
      delete form.type;
      delete form.min_quantity;
      delete form.max_quantity;
    }

    if (this.isIndividualPromotion) {
      delete form.amount;
      delete form.billing_cycles;
      delete form.currencies;
      delete form.currency_id;
      delete form.pricelists;
      delete form.type;
      delete form.min_quantity;
      delete form.max_quantity;
    }

    if (this.form.global) {
      form.products = [];
    }

    form.products = this.$_.map(form.products, product => {
      const beProduct = this.$store.getters[
        `data/${DataModules.CATALOGUE_PROMOTIONS}/mapToBESchema`
      ](product);
      this.$_.get(beProduct, "currency_id", "")
        ? delete beProduct.currencies
        : delete beProduct.currency_id;

      if (!this.isUpdate || this.promoTypeIsChanged) {
        delete beProduct.id;
      }
      return beProduct;
    });

    form.mandatory_products = this.$_.map(form.mandatory_products, product =>
      this.$store.getters[
        `data/${DataModules.CATALOGUE_PROMOTIONS}/mapToBESchema`
      ](product)
    );

    if (this.isUpdate) {
      delete form.mandatory_products;

      if (!this.promoTypeIsChanged && !this.selectedProductsChanged) {
        delete form.products;
      }
    }
    return this.$store.getters[
      `data/${DataModules.CATALOGUE_PROMOTIONS}/mapToBESchema`
    ](form);
  }

  // Methods
  public detachAllSelectedProducts() {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/detachAllSelectedProducts`,
      { promotionId: this.promotion?.id }
    );
  }

  public createPromotion(data) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/create`,
      {
        data
      }
    );
  }

  public syncSelectedProducts(promotionProducts) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/syncSelectedProducts`,
      {
        promotionId: this.promotionId,
        configurations: promotionProducts
      }
    );
  }

  public updatePromotion(data) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/update`,
      {
        promotionId: this.promotionId,
        data
      }
    );
  }

  public async revertToInitialState() {
    try {
      this.isFormInitiating = true;
      this.form = this.$_.cloneDeep(this.formClone);
      if (!this.isUpdate) {
        this.$set(this.form, "mandatory_products", []);
      }
    } finally {
      await this.$nextTick();
      this.isFormInitiating = false;
    }
  }

  public async validateBeforeSubmit() {
    if (!this.canManagePromotion) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      if (!this.isUpdate) {
        await this.createPromotion(this.formData);
        this.$store.dispatch("toast/show", {
          message: this.$t("_.object_successfully_created", {
            object: this.$t("_.promotion")
          })
        });
        this.$router.push({
          name: "admin__promotions"
        });
        return;
      }

      if (this.promoTypeIsChanged) {
        await this.detachAllSelectedProducts();
      }

      const updateFormData = this.formData;

      if (!this.promoTypeIsChanged && this.selectedProductsChanged) {
        await this.syncSelectedProducts(updateFormData.products);
        delete updateFormData.products;
      }

      await this.updatePromotion(updateFormData);

      this.$emit("reload-data");

      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.promotion")
        })
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public refreshForm() {
    if (this.isUpdate) {
      this.form = this.$store.getters[
        `data/${DataModules.CATALOGUE_PROMOTIONS}/mapPromotionToForm`
      ](this.promotion);
    }

    this.$nextTick(() => {
      this.$refs?.basicConfiguration?.resetState();
    });

    this.initialiseFormClone();
  }

  public initialiseFormClone() {
    this.formClone = this.$_.cloneDeep(this.form);
  }
}
